import * as React from "react"
import { useSherlock } from "api/useSherlock"
import type { AvailableCustomer, CustomerContextType, CustomerId } from "types/customer"
import { useAuth0 } from "@auth0/auth0-react"
import useAuth0Metadata from "api/useAuth0Metadata"
import { myCustomersQueryKey, useMyCustomers, useSetCurrentCustomer } from "api/useMyCustomers"
import { useQueryClient } from "@tanstack/react-query"
import { clearCustomerScopedCaches, seedBootCaches, useBootstrap } from "api/bootstrapQuery"
import { useBootSnapshot } from "boot/BootSnapshotProvider"
import { writeBootSnapshot } from "boot/snapshot"
import axios, { isAxiosError } from "axios"
import { useSearchParams } from "react-router-dom"
import config from "config"
import { isViewOnlyRefusal, VIEW_ONLY_EVENT } from "utils/viewOnly"
import { defaultCustomerId } from "hardcodes"
import { canResolveCustomer, resolveCustomer } from "./resolveCustomer"
import { setBasemapCustomer } from "utils/maplibregl/transform-request"

type Props = {
  children?: React.ReactNode
}

/**
 * Dev-only escape hatch: force the active customer via a `customerId` URL param
 * (e.g. `/nrol?customerId=NR`). The normal selection is persisted server-side
 * and shared across all sessions, so two local tabs can't run different
 * customers at once — this lets each local tab pin its own customer for testing
 * without touching the cloud-saved choice. Compiled out of production builds
 * (guarded by import.meta.env.DEV) and never persisted. The customer must still
 * be one the signed-in account is authorised for, or the API will reject it.
 */
export const getDevCustomerOverride = (): CustomerId | null => {
  if (!import.meta.env.DEV) return null
  try {
    const param = new URLSearchParams(window.location.search).get("customerId")
    return param ? (param.toUpperCase() as CustomerId) : null
  } catch {
    return null
  }
}

/**
 * `?customer=<ID>` on any link that left the app (emails: mentions,
 * assignments, access granted — rr-api utils/app-origin withCustomerParam)
 * names the customer the link belongs to. One account may use several
 * customers (ART-3130), so on load the app opens that customer when the
 * account may use it — and remembers it, like a picker switch — then drops
 * the parameter. A customer the account cannot use is ignored. Unlike the
 * dev-only `customerId` pin above, this works in production and persists.
 */
export const LINKED_CUSTOMER_PARAM = "customer"
export const readLinkedCustomer = (search: string): CustomerId | null => {
  try {
    const value = new URLSearchParams(search).get(LINKED_CUSTOMER_PARAM)?.trim().toUpperCase()
    return value && /^[A-Z0-9]{1,16}$/.test(value) ? (value as CustomerId) : null
  } catch {
    return null
  }
}

const CustomerContext = React.createContext<CustomerContextType>({
  customerId: "",
  availableCustomers: [],
  availableCustomersSettled: false,
  customerData: undefined,
  product: "roads",
  setCustomerId: () => undefined,
  addFeature: () => undefined,
  deleteFeature: () => undefined,
  addEmails: () => new Promise(() => undefined),
  deleteEmail: () => new Promise(() => undefined),
  addEmailsLoading: false,
  deleteEmailLoading: false,
  isAuthenticated: false,
  auth0IsLoading: false,
  customerDataIsLoading: false,
  customerDataError: null,
  profileSettled: false,
  customerResolved: false,
})

// Data fetched before this page load (a boot snapshot) must not be written
// back as if it were fresh.
const PAGE_LOADED_AT = Date.now()

const CustomerProvider = ({ children }: Props) => {
  // A boot snapshot opens the app on the customer this browser last used,
  // at once; the server's resolution below confirms it or moves the user.
  const { snapshot: bootSnapshot, optimistic } = useBootSnapshot()
  const [customerId, setCustomerIdState] = React.useState<CustomerId>(() => bootSnapshot?.customerId ?? "")
  // true while the active customer is the snapshot's guess rather than a
  // resolution or an explicit choice made in this session
  const fromSnapshotRef = React.useRef(!!bootSnapshot)
  const setCustomerId = React.useCallback((id: CustomerId) => {
    fromSnapshotRef.current = false
    setCustomerIdState(id)
  }, [])
  const queryClient = useQueryClient()

  // Get auth0 data. This should redirect to login, then back to our app.
  const { isLoading: auth0IsLoading, user, isAuthenticated } = useAuth0()
  const { userIsSuperAdmin, appMetadata, isMetadataSettled, profile, profileUpdatedAt } = useAuth0Metadata()

  // Signed in (or, with a boot snapshot, presumed so until Auth0 answers —
  // requests still wait for a real token).
  const loginSuccess = !!user && isAuthenticated

  // Dev-only: pin the active customer from the URL (read once on mount).
  const devCustomerOverride = React.useMemo(getDevCustomerOverride, [])

  // The customer an emailed link points at (read once on mount; see above).
  const [, setSearchParams] = useSearchParams()
  const linkedCustomer = React.useMemo(() => readLinkedCustomer(window.location.search), [])
  const setCurrentCustomer = useSetCurrentCustomer()

  // GET /me/bootstrap: the profile, the customer list and the active
  // customer's config in one request. The separate profile / list / config
  // requests below wait for it and only run if it fails.
  const bootstrapQuery = useBootstrap({ enabled: loginSuccess, preferredCustomer: linkedCustomer ?? devCustomerOverride })
  const bootstrapSettled = bootstrapQuery.isSuccess || bootstrapQuery.isError

  // ART-3130: the customers this account may use — in-app memberships ∪ the
  // token's home customer ∪ everything for super-admins — plus the persisted
  // picker selection. Settles to success or error; an error (or an empty
  // list) means "resolve exactly as before this feature existed" below.
  const myCustomersQuery = useMyCustomers({ enabled: loginSuccess && bootstrapSettled })
  // Answered during this page load (or failed): a boot snapshot's list is
  // shown at once but never counts as the answer — if /me/bootstrap fails, the
  // resolution and one-shot surfaces wait for /me/customers' own re-read
  // instead of settling on a list that may be days old.
  const myCustomersSettled = myCustomersQuery.isError || (myCustomersQuery.isSuccess && myCustomersQuery.dataUpdatedAt >= PAGE_LOADED_AT)
  const availableCustomers: AvailableCustomer[] = React.useMemo(() => {
    if (myCustomersQuery.data?.customers.length) return myCustomersQuery.data.customers
    if (!myCustomersQuery.isError) return []
    // Degraded mode (rr-api unreachable): every hardcoded customer for
    // super-admins, the default customer for everyone else. Not
    // authoritative ("legacy"): the topbar never evicts on it, and it
    // carries no per-customer settings.
    let fallbackIds: CustomerId[] = []
    if (userIsSuperAdmin) fallbackIds = config.customers
    else if (appMetadata?.customerID) fallbackIds = [appMetadata.customerID]
    return fallbackIds.map((id) => ({ customer_id: id, title: id, product: null, role: "member", source: "legacy" }))
  }, [myCustomersQuery.data, myCustomersQuery.isError, userIsSuperAdmin, appMetadata])

  // Resolve once per login: a later /me/customers answer (a focus refetch)
  // must not override the selection.
  const resolvedRef = React.useRef(false)
  // the same, for consumers: the active customer is this session's decision,
  // not (any more) a boot snapshot's guess
  const [customerResolved, setCustomerResolved] = React.useState(false)

  // set the correct customerId when login is complete
  React.useEffect(() => {
    if (!loginSuccess) {
      resolvedRef.current = false
      return
    }
    if (resolvedRef.current) return

    // Dev-only override takes precedence over the cloud-saved selection so a
    // local tab can pin its own customer (e.g. NR) for testing.
    if (devCustomerOverride) {
      console.log("[dev] forcing customerId from URL param:", devCustomerOverride)
      resolvedRef.current = true
      setCustomerId(devCustomerOverride)
      setCustomerResolved(true)
      return
    }

    // Fresh data first: with a boot snapshot the cached list is already
    // "settled", but the resolution must use what the server says now.
    if (!bootstrapSettled) return
    const memberships = myCustomersQuery.data
    if (!canResolveCustomer({ myCustomersSettled, memberships, profileSettled: isMetadataSettled })) return
    resolvedRef.current = true

    // A switch already applied in this session wins, whichever branch below
    // would otherwise run: a re-login within the session re-runs this
    // resolution, and the user's explicit choice must not be undone by the
    // fallback. The only exception is a list that POSITIVELY no longer
    // contains it (access revoked meanwhile).
    const knownIds = memberships?.customers.map((customer) => customer.customer_id) ?? []
    const positivelyGone = !!customerId && knownIds.length > 0 && !knownIds.includes(customerId)
    if (customerId && !fromSnapshotRef.current && !positivelyGone) {
      console.log("keeping the customer already selected this session", customerId)
      setCustomerResolved(true)
      return
    }

    console.log("resolving user's app", { user })

    // The link's customer is honoured only when the account may use it, and
    // is gone from the address bar either way.
    if (linkedCustomer) {
      setSearchParams(
        (params) => {
          params.delete(LINKED_CUSTOMER_PARAM)
          return params
        },
        { replace: true },
      )
    }

    const resolved = resolveCustomer({
      memberships,
      linkedCustomer,
      profileCustomerId: appMetadata?.customerID,
      defaultCustomerId,
    })
    console.log("resolved customer", resolved, { fromLink: linkedCustomer })
    // a link that moved the user remembers it, exactly like a picker switch
    if (resolved.persistLinked) {
      setCurrentCustomer.mutate(resolved.customerId, { onError: (error) => console.warn("could not persist the linked customer", error) })
    }
    if (fromSnapshotRef.current && customerId && resolved.customerId !== customerId) {
      // The snapshot opened another customer than the server now resolves
      // (switched on another device, access revoked): switch the way the
      // picker does — page caches are not customer-keyed, so clear them —
      // then put the fresh boot answer back so nothing is fetched twice.
      console.log("boot snapshot customer superseded", { snapshot: customerId, resolved: resolved.customerId })
      const boot = bootstrapQuery.data
      const next = resolved.customerId
      fromSnapshotRef.current = false
      // After this commit, like the picker's click handler: effects still to
      // run in it would re-create the old customer's queries after a clear.
      queueMicrotask(() => {
        clearCustomerScopedCaches(queryClient)
        if (boot) seedBootCaches(queryClient, boot)
        setCustomerId(next)
        setCustomerResolved(true)
      })
      return
    }
    setCustomerId(resolved.customerId)
    setCustomerResolved(true)
    // Deliberately resolve-once: only a login, the /me/customers call or (for
    // the fallback) the profile settling may re-run this; every other input
    // is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginSuccess, myCustomersSettled, isMetadataSettled, bootstrapSettled])

  // get customer data
  const sherlockQuery = useSherlock({
    customerId,
    enabled: loginSuccess && bootstrapSettled,
  })

  // No explicit refetch on a customer change: the config is keyed by
  // customer, and every switch clears the cache, so a new customer always
  // loads fresh — while an entry /me/bootstrap (or a boot snapshot) just put
  // in place is not fetched a second time.

  // ART-3130: a 401/403 on any request for the ACTIVE customer means the
  // gateway no longer admits it (access revoked since the list was fetched).
  // Refresh /me/customers straight away — the topbar then moves the user to
  // a customer they still hold — instead of waiting for the next focus
  // refetch while every request fails.
  const lastRefreshRef = React.useRef(0)
  const lastViewOnlyRef = React.useRef(0)
  React.useEffect(() => {
    if (!customerId) return undefined
    const prefix = `${config.railApiBasePath}/${customerId}/`
    const id = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // A viewer's write (403 view_only, any URL form): one notice, and the
        // role is refreshed so the write controls hide (useAccess).
        if (isViewOnlyRefusal(error)) {
          if (Date.now() - lastViewOnlyRef.current > 5_000) {
            lastViewOnlyRef.current = Date.now()
            window.dispatchEvent(new Event(VIEW_ONLY_EVENT))
            queryClient.invalidateQueries({ queryKey: myCustomersQueryKey })
          }
          return Promise.reject(error)
        }
        const status = isAxiosError(error) ? error.response?.status : undefined
        const url = isAxiosError(error) ? String(error.config?.url || "") : ""
        if ((status === 401 || status === 403) && url.startsWith(prefix) && Date.now() - lastRefreshRef.current > 10_000) {
          lastRefreshRef.current = Date.now()
          queryClient.invalidateQueries({ queryKey: myCustomersQueryKey })
        }
        return Promise.reject(error)
      },
    )
    return () => axios.interceptors.response.eject(id)
  }, [customerId, queryClient])

  // Basemap tile requests carry the active customer's own OS Data Hub key
  // (utils/maplibregl/transform-request); the transformer is module-level, so
  // it is told the customer here rather than per map.
  React.useEffect(() => {
    setBasemapCustomer(customerId)
  }, [customerId])

  // Remember what this session booted with, for the next page load to render
  // from (boot/snapshot.ts). Only data fetched during this page load, only
  // for the real signed-in user, and refreshed on every switch. Not while the
  // dev customerId pin is on (tab-only, never persisted), and not without a
  // profile: the app waits for one, so such a snapshot would bring the
  // splash back.
  const myCustomersUpdatedAt = myCustomersQuery.dataUpdatedAt
  const customerDataUpdatedAt = sherlockQuery.dataUpdatedAt
  React.useEffect(() => {
    if (optimistic || !isAuthenticated || !user?.sub || !customerId || devCustomerOverride) return
    const myCustomers = myCustomersQuery.data
    const customer = sherlockQuery.data
    // the profile too must have been (re)read or written this page load
    if (!profile || profileUpdatedAt < PAGE_LOADED_AT) return
    if (!myCustomers || !customer || myCustomersUpdatedAt < PAGE_LOADED_AT || customerDataUpdatedAt < PAGE_LOADED_AT) return
    if (!myCustomers.customers.some((entry) => entry.customer_id === customerId)) return
    writeBootSnapshot({
      savedAt: Date.now(),
      sub: user.sub,
      user,
      customerId,
      profile,
      myCustomers,
      customer,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optimistic, isAuthenticated, user, customerId, myCustomersUpdatedAt, customerDataUpdatedAt, profileUpdatedAt])

  // ART-3130: tell Intercom which customer this person is working in, on
  // resolution and on every switch. The support bot (cloud-pipeline intercom
  // worker → rr-api /fleet/resolve?customer=) uses it to scope fleet, admins
  // and investigations to the ACTIVE customer rather than the Auth0 home
  // claim. It is set from the browser, so rr-api only honours it when the
  // account actually holds that customer.
  const customerTitle = sherlockQuery.data?.customerTitle
  React.useEffect(() => {
    if (!customerId || typeof window.Intercom !== "function") return
    try {
      window.Intercom("update", {
        current_customer_id: customerId,
        current_customer_name: customerTitle ?? customerId,
        company: { company_id: customerId, name: customerTitle ?? customerId },
      })
    } catch (error) {
      console.warn("Intercom update failed", error)
    }
  }, [customerId, customerTitle])

  const value: CustomerContextType = {
    customerId,
    availableCustomers,
    // The server's answer, this page load: a list seeded from a boot snapshot
    // is shown (availableCustomers) but does not count — surfaces that act
    // once on per-customer settings (useMySettings) must not act on it.
    availableCustomersSettled: myCustomersSettled && bootstrapSettled,
    customerResolved,
    setCustomerId,
    customerData: sherlockQuery.data,
    product: sherlockQuery.data?.product || "rail",
    addFeature: sherlockQuery.addFeatureMutation.mutate,
    deleteFeature: sherlockQuery.deleteFeatureMutation.mutate,
    addEmails: sherlockQuery.addEmailsMutation.mutateAsync,
    deleteEmail: sherlockQuery.deleteEmailMutation.mutateAsync,
    addEmailsLoading: sherlockQuery.addEmailsMutation.isPending,
    deleteEmailLoading: sherlockQuery.deleteEmailMutation.isPending,
    isAuthenticated,
    auth0IsLoading,
    // First load only (no data yet). A background refetch must not count:
    // AppWrapper swaps the whole app for a spinner while this is true, which
    // would unmount every page mid-session.
    customerDataIsLoading: sherlockQuery.isLoading,
    // Only when there is no config to show: a failed background refetch
    // keeps the data it had (react-query v5) and must not replace a working
    // app with the full-screen error.
    customerDataError: sherlockQuery.isError && !sherlockQuery.data && sherlockQuery.error,
    profileSettled: isMetadataSettled,
  }

  return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>
}

const useCustomerData = () => {
  const context = React.useContext(CustomerContext)

  if (context === undefined) {
    throw new Error("useCustomerData must be used within the CustomerProvider")
  }

  return context
}

export { CustomerProvider, useCustomerData }
